import { useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import { getUTMParams } from '../../../features/quickstart/utils/utmCapture';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

function buildSteamCardUrl(): string {
  const utms = getUTMParams();
  const params = new URLSearchParams();
  if (utms.utm_source) params.set('utm_source', utms.utm_source);
  else params.set('utm_source', 'playcastlanding');
  if (utms.utm_medium) params.set('utm_medium', utms.utm_medium);
  if (utms.utm_campaign) params.set('utm_campaign', utms.utm_campaign);
  if (utms.utm_term) params.set('utm_term', utms.utm_term);
  if (utms.utm_content) params.set('utm_content', utms.utm_content);
  return `/host/steam-card?${params.toString()}`;
}

/**
 * SecurityControl Component
 * Background card with flipped decorative image, two info columns
 * (SECURITY + CONTROL), a cycling headline "YOUR DEVICE./CONTROL./PRIVACY.",
 * and two CTA buttons.
 * Desktop: Absolute positioned layout within 1024px scaler
 * Mobile: Stubbed for future implementation
 */

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const BG_IMAGE_URL =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-068949da-e324-4bc6-8b8a-c7973e395e16.png?e=webp&nll=true&cX=355&cY=617&cW=1377&cH=410';

const CYCLING_WORDS = ['DEVICE.', 'CONTROL.', 'PRIVACY.'];

const columnTitleStyle: React.CSSProperties = {
  lineHeight: '21px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '20px',
  color: WHITE,
  margin: 0,
  fontVariationSettings: "'wght' 900",
};

const columnBodyStyle: React.CSSProperties = {
  lineHeight: '13px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '10px',
  color: WHITE,
  margin: 0,
};

export const SecurityControl = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const navigate = useNavigate();
  const location = useLocation();

  const goToNonsoleMode = useCallback(() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Learn More', buttonLocation: 'SecurityControl' }); navigate('/nonsole-mode'); }, [navigate]);
  const goToSteamCard = useCallback(() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Network', buttonLocation: 'SecurityControl' }); if (location.pathname === '/hosts') { window.scrollTo({ top: 0, behavior: 'smooth' }); } else { navigate('/hosts'); } }, [navigate, location.pathname]);

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '250px',
          backgroundColor: 'transparent',
          overflow: 'visible',
          position: 'relative',
          paddingBottom: '16px',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Background Image (flipped vertically) */}
          <div
            style={{
              position: 'absolute',
              left: '123px',
              top: '0px',
              width: '786px',
              height: '234px',
              borderRadius: '20px',
              padding: '1px',
              backgroundColor: 'rgba(175, 168, 255, 0.3)',
              zIndex: 1,
              boxShadow: '0px -1px 12px rgba(114, 137, 218, 0.15)',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '19px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  transform: 'scaleY(-1)',
                  backgroundImage: `url(${BG_IMAGE_URL})`,
                  backgroundPosition: '50% 50%',
                  backgroundSize: 'cover',
                  opacity: 0.9,
                }}
              />
            </div>
          </div>

          {/* SECURITY Column */}
          <div
            style={{
              position: 'absolute',
              left: '570px',
              top: '49px',
              width: '134px',
              zIndex: 5,
            }}
          >
            <p style={columnTitleStyle}>SECURITY</p>
            <div style={{ height: '13px' }} />
            <p style={columnBodyStyle}>
              Every broadcast runs inside a secure, isolated guest environment
              — keeping your personal data and accounts completely safe.
            </p>
          </div>

          {/* CONTROL Column */}
          <div
            style={{
              position: 'absolute',
              left: '723px',
              top: '48px',
              width: '134px',
              zIndex: 5,
            }}
          >
            <p style={columnTitleStyle}>CONTROL</p>
            <div style={{ height: '13px' }} />
            <p style={columnBodyStyle}>
              You&apos;re always in control. End a broadcast whenever you like;
              whether from your host machine or remotely from anywhere.
            </p>
          </div>

          {/* Cycling Headline: YOUR DEVICE./CONTROL./PRIVACY. */}
          <div
            style={{
              position: 'absolute',
              left: '154px',
              top: '93px',
              width: '431px',
              height: '49px',
              zIndex: 5,
              overflow: 'hidden',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '45px',
              lineHeight: '50px',
              color: WHITE,
              fontVariationSettings: "'wght' 900",
            }}
          >
            <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
              YOUR
              <span
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  marginLeft: '4px',
                }}
              >
                <span
                  style={{ visibility: 'hidden', pointerEvents: 'none' }}
                >
                  PRIVACY.
                </span>
                {CYCLING_WORDS.map((word, i) => (
                  <span
                    key={word}
                    className="cycle-word"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      color: YELLOW,
                      opacity: 0,
                      animation: 'cycleWord 6s infinite',
                      animationDelay: `${i * 2}s`,
                    }}
                  >
                    {word}
                  </span>
                ))}
              </span>
            </span>
          </div>

          {/* Learn More Button */}
          <button
            className="aida-button aida-button-outline-fill"
            onClick={goToNonsoleMode}
            style={{
              position: 'absolute',
              left: '570px',
              top: '160px',
              width: '124px',
              height: '22px',
              borderRadius: '30px',
              zIndex: 5,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '22px',
              cursor: 'pointer',
            }}
          >
            <span
              className="text"
              style={{ height: '22px', lineHeight: '22px' }}
            >
              Learn More
            </span>
          </button>

          {/* Join the Network Button */}
          <button
            className="aida-button"
            onClick={goToSteamCard}
            style={{
              position: 'absolute',
              left: '723px',
              top: '160px',
              width: '124px',
              height: '22px',
              borderRadius: '30px',
              zIndex: 5,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '22px',
              cursor: 'pointer',
              background: '#FBBF24',
              color: '#161423',
            }}
          >
            <span
              className="text"
              style={{ height: '22px', lineHeight: '22px' }}
            >
              Join the Network
            </span>
          </button>
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '16px 20px 40px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Card container with background image */}
        <motion.div
          style={{
            position: 'relative',
            width: '340px',
            borderRadius: '20px',
            padding: '1px',
            backgroundColor: 'rgba(175, 168, 255, 0.3)',
            boxShadow: '0px -1px 12px rgba(114, 137, 218, 0.15)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div
            style={{
              width: '100%',
              borderRadius: '19px',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Flipped background image */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                transform: 'scaleY(-1)',
                backgroundImage: `url(${BG_IMAGE_URL})`,
                backgroundPosition: 'left center',
                backgroundSize: 'auto 100%',
                opacity: 0.9,
              }}
            />

            {/* Card content */}
            <div
              style={{
                position: 'relative',
                zIndex: 5,
                padding: '40px 36px 40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* Cycling Headline: YOUR / DEVICE./CONTROL./PRIVACY. */}
              <div
                style={{
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 900,
                  color: WHITE,
                  fontVariationSettings: "'wght' 900",
                }}
              >
                <div
                  style={{
                    fontSize: '48px',
                    lineHeight: '54px',
                  }}
                >
                  YOUR
                </div>
                <div
                  style={{
                    position: 'relative',
                    fontSize: '48px',
                    lineHeight: '54px',
                    height: '54px',
                    marginTop: '2px',
                  }}
                >
                  <span style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                    PRIVACY.
                  </span>
                  {CYCLING_WORDS.map((word, i) => (
                    <span
                      key={word}
                      className="cycle-word"
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        textAlign: 'center',
                        color: YELLOW,
                        opacity: 0,
                        animation: 'cycleWord 6s infinite',
                        animationDelay: `${i * 2}s`,
                      }}
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>

              {/* SECURITY Section + Learn More */}
              <div style={{ width: '100%', marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <p style={{ ...columnTitleStyle, textAlign: 'center', fontSize: '30px', lineHeight: '34px' }}>SECURITY</p>
                <div style={{ height: '8px' }} />
                <p style={{ ...columnBodyStyle, textAlign: 'center', maxWidth: '280px', fontSize: '16px', lineHeight: '22px' }}>
                  Every broadcast runs inside a secure, isolated guest environment
                  — keeping your personal data and accounts completely safe.
                </p>
                <button
                  className="aida-button aida-button-outline-fill"
                  onClick={goToNonsoleMode}
                  style={{
                    width: '207px',
                    height: '35px',
                    borderRadius: '44px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '35px',
                    cursor: 'pointer',
                    marginTop: '12px',
                  }}
                >
                  <span
                    className="text"
                    style={{ height: '35px', lineHeight: '35px' }}
                  >
                    Learn More
                  </span>
                </button>
              </div>

              {/* CONTROL Section + Join the Network */}
              <div style={{ width: '100%', marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <p style={{ ...columnTitleStyle, textAlign: 'center', fontSize: '30px', lineHeight: '34px' }}>CONTROL</p>
                <div style={{ height: '8px' }} />
                <p style={{ ...columnBodyStyle, textAlign: 'center', maxWidth: '280px', fontSize: '16px', lineHeight: '22px' }}>
                  You&apos;re always in control. End a broadcast whenever you like;
                  whether from your host machine or remotely from anywhere.
                </p>
                <button
                  className="aida-button"
                  onClick={goToSteamCard}
                  style={{
                    width: '207px',
                    height: '35px',
                    borderRadius: '44px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '35px',
                    cursor: 'pointer',
                    background: '#FBBF24',
                    color: '#161423',
                    marginTop: '12px',
                  }}
                >
                  <span
                    className="text"
                    style={{ height: '35px', lineHeight: '35px' }}
                  >
                    Join the Network
                  </span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>
    </>
  );
};
